/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  LinearProgress,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TimelineIcon from "@mui/icons-material/Timeline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NotesIcon from "@mui/icons-material/Notes"; // New
import TimerIcon from "@mui/icons-material/Timer"; // New
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { dashboard } from "./services/AppService";
import { useNavigate, Link } from "react-router-dom";
import UsageGuide from "./UsageGuide";

const DashboardCard = ({ title, value, icon, link }) => (
  <Link to={link} style={{ textDecoration: "none" }}>
    <Card
      sx={{
        p: 3,
        borderRadius: 5,
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s ease",
        bgcolor: "background.paper",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <Avatar
        sx={{
          bgcolor: "primary.light",
          color: "primary.main",
          width: 60,
          height: 60,
          mb: 2,
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="h6" sx={{ color: "text.primary", mb: 0.5 }}>
        {title}
      </Typography>
      {value !== undefined && (
        <Typography
          variant="h3"
          sx={{ color: "primary.main", fontWeight: "bold", fontSize: { xs: "1.8rem", md: "2.5rem" } }}
        >
          {value}
        </Typography>
      )}
    </Card>
  </Link>
);

const TrackerCard = ({ title, progress, icon }) => (
  <Card
    sx={{
      p: 3,
      borderRadius: 4,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      bgcolor: "background.paper",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar sx={{ bgcolor: "secondary.light", color: "secondary.main" }}>{icon}</Avatar>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ width: "100%", mt: 1 }}>
      <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {progress}% completed
      </Typography>
    </Box>
  </Card>
);

const sampleChartData = [
  { date: "Day 1", topics: 3 },
  { date: "Day 2", topics: 4 },
  { date: "Day 3", topics: 5 },
  { date: "Day 4", topics: 6 },
  { date: "Day 5", topics: 8 },
  { date: "Day 6", topics: 7 },
  { date: "Day 7", topics: 9 },
];

function AppHome() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    totalTopics: 0,
    pendingRevision: 0,
    memorizedTopics: 0,
  });

  useEffect(() => {
    document.body.style.backgroundColor = "#F9FAFB";
    fetchDashboardData();
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboard();
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        if (response.status === 401) {
          console.error("Unauthorized access - logging out user");
          navigate("/auth/logout");
        }
        console.error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const cards = [
    {
      title: "Total Topics",
      value: dashboardData.totalTopics,
      icon: <FormatListNumberedIcon sx={{ fontSize: 30 }} />,
      link: "/app/topics",
    },
    {
      title: "Pending Recall",
      value: dashboardData.pendingRevision,
      icon: <TimelineIcon sx={{ fontSize: 30 }} />,
      link: "/app/topics",
    },
    {
      title: "Memorized Topics",
      value: dashboardData.memorizedTopics,
      icon: <ScheduleIcon sx={{ fontSize: 30 }} />,
      link: "/app/topics",
    },
    // {
    //   title: "Notes",
    //   icon: <NotesIcon sx={{ fontSize: 30 }} />,
    //   link: "/app/notes",
    // },
    {
      title: "Pomodoro Timer",
      icon: <TimerIcon sx={{ fontSize: 30 }} />,
      link: "/app/timer",
    },
    // Add more tools here easily
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Welcome Message */}
      <Box sx={{ mb: 6, textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          Welcome back! 👋
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Here’s a quick snapshot of your learning journey
        </Typography>
      </Box>

      {/* Usage Guide */}
      <Box sx={{ mb: 8 }}>
        <UsageGuide />
      </Box>

      {/* Dashboard Cards */}
      <Grid container spacing={2} sx={{ mb: 6 }}>
        {cards.map((card, idx) => (
          <Grid item key={idx} xs={12} sm={4} md={3}>
            <DashboardCard
              title={card.title}
              value={card.value}
              icon={card.icon}
              link={card.link}
            />
          </Grid>
        ))}
      </Grid>


      {/* Progress Tracker */}
      <Typography className="d-none" variant="h5" sx={{ mb: 2 }}>
        Your Progress
      </Typography>
      <Card className="d-none"
        sx={{
          mb: 6,
          p: 4,
          borderRadius: 5,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          bgcolor: "background.paper",
        }}
      >
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={sampleChartData}>
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="topics"
              stroke="#1976d2"
              strokeWidth={3}
              dot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Mini Trackers */} 
      <Grid className="d-none" container spacing={4}>
        <Grid item xs={12} sm={6}>
          <TrackerCard
            title="Today's Goal Progress"
            progress={70}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TrackerCard
            title="Current Streak"
            progress={90}
            icon={<EmojiEventsIcon />}
          />
        </Grid>
      </Grid>
      
    </Container>
  );
}

export default AppHome;
